import { Avatar, Button, Icon, Popover, PopoverItem, Skeleton, useToast } from "@helmr/ui";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isApiError } from "../../api/client";
import { moveLead, type Lead, type LeadFilters, type Stage } from "../../api/leads";
import { useServices } from "../../app/services";
import { FailureBody } from "../home/states";
import { ConvertDrawer } from "./ConvertDrawer";
import { flatten, useInvalidateLeadData, useLeadPages, useStages, useStaffNames, type Scope } from "./hooks";
import { leads as t } from "./strings";
import { LeadsEmpty, SourceChip, leadName } from "./ui";
import s from "./leads.module.css";

interface Override { lead: Lead; to: string }
type Staff = ReturnType<typeof useStaffNames>;

export function Board({ sc, filters, hasFilters }: { sc: Scope; filters: LeadFilters; hasFilters: boolean }) {
  const stages = useStages(sc);
  const staff = useStaffNames(sc);
  const { client } = useServices();
  const refresh = useInvalidateLeadData();
  const toast = useToast();
  const [overrides, setOverrides] = useState<Map<string, Override>>(new Map());
  const [message, setMessage] = useState<string | null>(null);
  const [convert, setConvert] = useState<{ lead: Lead; stage: Stage } | null>(null);
  const dragging = useRef<Lead | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const canMove = sc.ctx.has("leads.move");

  const clear = (id: string) => setOverrides((m) => { const n = new Map(m); n.delete(id); return n; });

  async function move(lead: Lead, to: Stage) {
    const current = overrides.get(lead.id)?.to ?? lead.stageId;
    if (current === to.id) return;
    setMessage(null);
    // A lead becomes won once it is a member (the API refuses otherwise): open Convert instead of a doomed request.
    if (to.type === "won" && !lead.memberId) {
      if (sc.ctx.has("leads.convert") && sc.ctx.has("leads.edit")) setConvert({ lead, stage: to }); else setMessage(t.board.refused(t.convert.needsRole));
      return;
    }
    setOverrides((m) => new Map(m).set(lead.id, { lead, to: to.id }));
    try {
      await moveLead(client, lead.id, to.id);
      toast.show(t.board.moved(leadName(lead), to.name));
      await refresh();
    } catch (err) {
      const why = isApiError(err) ? err.message : "Check your connection and try again.";
      setMessage(t.board.refused(why));
    } finally {
      clear(lead.id); // success: the refetch now holds the truth; failure: the card snaps back
    }
  }

  const list = stages.data ?? [];
  // One cheap read tells an empty pipeline (point at Settings) from a column with nothing in it.
  const anyLead = useLeadPages(sc, filters);
  if (stages.isPending) return <BoardSkeleton />;
  if (stages.error) return <FailureBody error={stages.error} title={t.error} what="The pipeline" onRetry={() => void stages.refetch()} />;

  if (anyLead.data && flatten(anyLead.data.pages).length === 0) return <div className={s.boardCard}><LeadsEmpty sc={sc} filtered={hasFilters} /></div>;

  return (
    <div className={s.boardWrap}>
      {message ? <div className={s.alert} role="alert"><span>{message}</span><button type="button" onClick={() => setMessage(null)}>Dismiss</button></div> : null}
      {canMove ? <p className={s.hint}>{t.board.dragHint}</p> : null}
      <div className={s.board} role="group" aria-label={t.board.label}>
        {list.map((stage) => (
          <Column
            key={stage.id}
            sc={sc}
            stage={stage}
            stages={list}
            filters={filters}
            staff={staff}
            overrides={overrides}
            canMove={canMove}
            over={over === stage.id}
            hasFilters={hasFilters}
            onMove={move}
            onDragStart={(l) => { dragging.current = l; }}
            onOver={(on) => setOver((cur) => (on ? stage.id : cur === stage.id ? null : cur))}
            onDrop={() => { const l = dragging.current; dragging.current = null; setOver(null); if (l && canMove) void move(l, stage); }}
          />
        ))}
      </div>
      {convert ? (
        <ConvertDrawer
          sc={sc}
          lead={convert.lead}
          stages={list}
          wonStage={convert.stage}
          open
          onClose={() => setConvert(null)}
          onConverted={() => setConvert(null)}
        />
      ) : null}
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className={s.board} aria-busy="true" data-testid="board-skeleton">
      {[0, 1, 2, 3].map((c) => (
        <div className={s.col} key={c}>
          <div className={s.colHead}><Skeleton width="50%" height={14} /></div>
          <div className={s.cards}>{[0, 1, 2].map((i) => <div className={s.skCard} key={i}><Skeleton width="60%" height={13} /><Skeleton width="85%" height={12} /></div>)}</div>
        </div>
      ))}
    </div>
  );
}

interface ColumnProps {
  sc: Scope; stage: Stage; stages: Stage[]; filters: LeadFilters; staff: Staff; overrides: Map<string, Override>; canMove: boolean; over: boolean; hasFilters: boolean;
  onMove: (l: Lead, to: Stage) => void; onDragStart: (l: Lead) => void; onOver: (on: boolean) => void; onDrop: () => void;
}

function Column({ sc, stage, stages, filters, staff, overrides, canMove, over, hasFilters, onMove, onDragStart, onOver, onDrop }: ColumnProps) {
  const pages = useLeadPages(sc, { ...filters, stage: stage.id });
  const server = flatten(pages.data?.pages);
  const cards = useMemo(() => {
    const incoming = [...overrides.values()].filter((o) => o.to === stage.id && !server.some((l) => l.id === o.lead.id)).map((o) => o.lead);
    const stay = server.filter((l) => { const o = overrides.get(l.id); return !o || o.to === stage.id; });
    return [...incoming, ...stay];
  }, [server, overrides, stage.id]);
  const more = !!pages.hasNextPage;

  return (
    <section
      className={s.col}
      aria-label={stage.name}
      data-over={over || undefined}
      onDragOver={canMove ? (e) => { e.preventDefault(); onOver(true); } : undefined}
      onDragLeave={canMove ? () => onOver(false) : undefined}
      onDrop={canMove ? (e) => { e.preventDefault(); onDrop(); } : undefined}
    >
      <div className={s.colHead}>
        <h2><span title={stage.name}>{stage.name}</span></h2>
        {pages.data ? <span className={s.count} aria-label={`${cards.length}${more ? " or more" : ""} leads`}>{t.board.count(cards.length, more)}</span> : null}
      </div>
      <div className={s.cards} aria-busy={pages.isPending || undefined}>
        {pages.isPending ? [0, 1].map((i) => <div className={s.skCard} key={i}><Skeleton width="60%" height={13} /><Skeleton width="85%" height={12} /></div>) : pages.error ? (
          <FailureBody error={pages.error} title={t.error} what="Leads" onRetry={() => void pages.refetch()} />
        ) : cards.length === 0 ? (
          <div className={s.colEmpty}>{hasFilters ? t.empty.noMatch : t.empty.column}</div>
        ) : cards.map((l) => (
          <Card key={l.id} lead={l} stage={stage} stages={stages} staff={staff} canMove={canMove} pending={overrides.has(l.id)} onMove={onMove} onDragStart={onDragStart} />
        ))}
      </div>
      {more ? <div className={s.colFoot}><Button size="sm" onClick={() => void pages.fetchNextPage()} busy={pages.isFetchingNextPage} busyLabel="Loading…">{t.loadMore}</Button></div> : null}
    </section>
  );
}

function Card({ lead, stage, stages, staff, canMove, pending, onMove, onDragStart }: { lead: Lead; stage: Stage; stages: Stage[]; staff: Staff; canMove: boolean; pending: boolean; onMove: (l: Lead, to: Stage) => void; onDragStart: (l: Lead) => void }) {
  const name = leadName(lead);
  const owner = staff.nameOf(lead.ownerId);
  return (
    <article className={s.card} draggable={canMove} data-pending={pending || undefined} data-testid="lead-card" onDragStart={canMove ? (e) => { e.dataTransfer.setData("text/plain", lead.id); e.dataTransfer.effectAllowed = "move"; onDragStart(lead); } : undefined}>
      <div className={s.cardTop}>
        <Avatar id={lead.id} name={name} size="xs" />
        <Link to={`/leads/${lead.id}`} aria-label={t.board.openProfile(name)}><b title={name}>{name}</b></Link>
        {canMove ? (
          <Popover
            role="menu"
            label={t.board.moveMenu(name)}
            trigger={(tp) => <button {...tp} className={s.moveBtn} aria-label={t.board.moveLabel(name)}>{t.board.moveTo}</button>}
          >
            {({ close }) => stages.filter((x) => x.id !== stage.id).map((x) => (
              <PopoverItem key={x.id} onClick={() => { close(); onMove(lead, x); }} lead={x.type === "won" ? <Icon name="check" /> : undefined}>{x.name}</PopoverItem>
            ))}
          </Popover>
        ) : null}
      </div>
      <div className={s.cardMeta}>
        <SourceChip source={lead.source} />
        <span title={owner ?? undefined}>{lead.ownerId ? (owner ?? "") : t.noOwner}</span>
      </div>
    </article>
  );
}
